// CONTRACT:C2-RENDER-ENGINE.1.0
/** GLOBAL RANK — the ONE doctrine-preserving clutter-priority ordinal for labels AND icons
 *  (DESIGN-CLUTTER-PRIORITY v2 §1.1). Before this, labels used a raw affiliation rank while
 *  icons used an alert-aware variant — two orders, one screen. This module owns the single
 *  order, pure and total, so every declutter surface (label sort, density cull, latch stack
 *  order, fast-grid slot priority, icon-density LOD) degrades the SAME way:
 *
 *    labels:  0 emphasized (selected || hovered)   icons: (no emphasis tier — LOD stays
 *             1 alert  (kind === "alert")                 hover-independent; see below)
 *             2 friend (own force FIRST — Will's          1 alert
 *               shipped placement doctrine)               2 friend
 *             3 hostile                                   3 hostile
 *             4 unknown / pending / unrecognized          4 unknown / pending
 *             5 neutral bulk                              5 neutral bulk
 *
 *  DOCTRINE: friend stays ABOVE hostile ("own force, then threats" — settled placement
 *  doctrine, not relitigated); alerts crown both (a neutral NWS tornado is not neutral
 *  clutter). Emphasis is INTERACTION state, not envelope data — the host supplies it, and
 *  the icon variant deliberately EXCLUDES it (marker colour/size updateTriggers exclude
 *  interaction, so a hover-dependent icon rank would render stale).
 *
 *  Also home to the two LABEL GATES (v2 §1.2) and the icon-LOD multiplier math (v2 §1.1
 *  divisor fix), so the whole clutter-priority policy is T0-testable pure math.
 *
 *  VISIBILITY LAW (I13): presentation ONLY. Rank/gate outcomes change which LABELS draw and
 *  how icons scale/fade — never WHAT the projection renders; every entity stays rendered and
 *  pickable. PURE: imports nothing, deterministic, total on garbage input. Tested in
 *  packages/render-engine/test/globalRank.test.ts. */

/** The highest (least prominent) rank — the neutral-bulk tier. The icon-density LOD divides
 *  by THIS (never a hardcoded /4): with ranks bounded to [0, GLOBAL_RANK_MAX] and the tuning
 *  bounds (iconLodShrink ≤ 0.8, iconLodFade ≤ 0.9), the multiplier floors stay ≥ 0.2 size /
 *  ≥ 0.1 alpha — no zero-size icons, no negative alpha, ever (the v1 /4 hazard). */
export const GLOBAL_RANK_MAX = 5;

/** The shared kind/affiliation tier [1..GLOBAL_RANK_MAX]. Alerts crown (any affiliation —
 *  real alert feeds like NWS/USGS arrive NEUTRAL); a missing/unrecognized affiliation ranks
 *  with unknown (fail toward prominence, not toward the neutral drop-first tier). */
function affTier(kind: unknown, affiliation: unknown): number {
  if (kind === "alert") return 1;
  switch (affiliation) {
    case "friend":
      return 2; // own force FIRST — before threats (doctrine)
    case "hostile":
      return 3;
    case "neutral":
      return GLOBAL_RANK_MAX; // 5 — the bulk haystack, first to thin
    default:
      return 4; // unknown / pending / missing / garbage
  }
}

/** LABEL rank [0..GLOBAL_RANK_MAX]: 0 emphasized → 1 alert → 2 friend → 3 hostile →
 *  4 unknown/pending → 5 neutral. Lower = placed first, culled last, stacked higher. */
export function globalRank(kind: unknown, affiliation: unknown, emphasized: boolean): number {
  return emphasized ? 0 : affTier(kind, affiliation);
}

/** ICON rank [1..GLOBAL_RANK_MAX] — the emphasis-free variant for the icon-density LOD
 *  (rank 0 does not exist here; hover/selection never re-ranks an icon). */
export function globalRankIcon(kind: unknown, affiliation: unknown): number {
  return affTier(kind, affiliation);
}

/** ICON-DENSITY LOD multiplier: 1 − densityFactor·maxCut·(rank/GLOBAL_RANK_MAX), CLAMPED to
 *  the floor 1 − maxCut (v2 §1.1 divisor fix — the shipped /4 divisor met rank 5 at the
 *  tuning bounds as size 0.0 and NEGATIVE alpha). At the max tuning values (densityFactor 1,
 *  iconLodShrink 0.8 / iconLodFade 0.9) the rank-5 floor is the documented ≥0.2 size /
 *  ≥0.1 alpha. Total: never NaN/negative, never > 1; densityFactor ≤ 0 or maxCut ≤ 0 ⇒ 1
 *  (LOD off). The blueForceAlwaysProminent friend EXEMPTION is the CALLER's branch (an
 *  explicit exemption — the old "rank 0 is mathematically immune" story no longer exists). */
export function densityLodMul(rank: number, densityFactor: number, maxCut: number): number {
  if (!(densityFactor > 0) || !(maxCut > 0)) return 1;
  const floor = Math.max(0, 1 - maxCut); // the documented floor at full density, lowest tier
  const m = 1 - densityFactor * maxCut * (rank / GLOBAL_RANK_MAX);
  if (!Number.isFinite(m)) return 1; // total on garbage — never poison a size/alpha accessor
  return m < floor ? floor : m > 1 ? 1 : m;
}

/** THE NAME-FALLBACK GATE (v2 §1.2 — the label flood's biggest lever). Governs ONLY tracks
 *  with a `name` but no real callsign: opens when the view is SPARSE (room exists — a lone
 *  AIS vessel or weather alert reads), the track is EMPHASIZED (hovered/selected — names stay
 *  one interaction away, "clean but complete"), the track is tactically significant
 *  (friend/hostile), or it is an ALERT (kind === "alert" — real alert feeds are neutral and
 *  callsign-less; without this seam a NWS tornado could never label). The old
 *  source === "tak/live" opener is GONE: on prod EVERY entity is tak/live, so it opened the
 *  gate to the entire neutral haystack (Will's Hormuz label-flood screenshot). */
export function nameFallbackOpens(
  sparseView: boolean,
  emphasized: boolean,
  affiliation: unknown,
  kind: unknown,
): boolean {
  return (
    sparseView ||
    emphasized ||
    affiliation === "friend" ||
    affiliation === "hostile" ||
    kind === "alert"
  );
}

/** THE ZOOM-GATE BYPASS (v2 §1.2): which labels draw even when the zoom declutter says
 *  labels are OFF. Friendly callsigns always read (doctrine), an emphasized track always
 *  keeps its label, and an ALERT whose glyph shows deserves its label below zoom 7 too.
 *  Hostile is deliberately NOT bypassed here — unchanged shipped behavior. */
export function labelZoomGateOpen(
  labelsVisible: boolean,
  emphasized: boolean,
  affiliation: unknown,
  kind: unknown,
): boolean {
  return labelsVisible || emphasized || affiliation === "friend" || kind === "alert";
}
